<template>
  <div>
    <header class="flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">Mark Attendance</h2>
        <p v-if="roster" class="m-0 pt-1 text-xs text-300">
          Roster #{{ roster.roster_id }} · {{ roster.employee_name }}
        </p>
      </div>
      <Button label="Back" icon="pi pi-arrow-left" class="surface-300 hover:surface-500" @click="handleBack" />
    </header>

    <div v-if="isLoading" class="card text-300">Loading roster…</div>
    <div v-else-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">
      <DataTable :value="attendanceRows" aria-label="Mark Attendance Table">
        <Column field="employee_name" header="Employee" class="text-600 w-3 pl-3">
          <template #body="slotProps">
            <div class="flex items-center gap-2 pl-1">
              <Button
                icon="pi pi-pencil"
                class="flex text-xs w-2rem surface-200 hover:surface-400 mr-1"
                :disabled="submitting"
                @click="openCompanyDialog"
              />
              <div>
                <p class="block font-bold pb-1 m-0 p-0">{{ slotProps.data.employee_name }}</p>
                <p class="text-xs text-300 m-0 p-0 pb-1">{{ slotProps.data.employee_company_name }}</p>
                <p class="text-xs text-300 mr-2 m-0 p-0">
                  <span class="mr-2">@{{ slotProps.data.employee_id }}</span>
                  <span class="mr-2">{{ slotProps.data.employee_status }}</span>
                  <span>{{ slotProps.data.employee_department }}</span>
                </p>
              </div>
            </div>
          </template>
        </Column>

        <Column field="shift_type" header="Type" class="text-600 w-1">
          <template #body="slotProps">
            <div>
              <p class="block pb-1 m-0 p-0">
                <Tag
                  severity="info"
                  :class="{
                    'bg-cyan-800': slotProps.data.shift_type === 'COVER',
                    'surface-300': slotProps.data.shift_type === 'SHIFT',
                    'bg-green-800': slotProps.data.shift_type === 'ROSTER'
                  }"
                  style="letter-spacing: 2px;"
                >
                  {{ slotProps.data.shift_type }}
                </Tag>
              </p>
              <p class="block text-xs text-300 m-0 p-0">
                {{ slotProps.data.event_id ? `ID #${slotProps.data.event_id}` : 'NEW ENTRY' }}
              </p>
            </div>
          </template>
        </Column>

        <Column field="event_day" header="Day" class="text-600 w-1">
          <template #body="slotProps">
            <div>
              <p class="block pb-1 m-0 p-0">{{ slotProps.data.event_day }}</p>
              <p class="block text-xs text-300 m-0 p-0">{{ slotProps.data.event_month }}</p>
            </div>
          </template>
        </Column>

        <Column field="event_date" header="Date" class="text-600 w-3">
          <template #body="slotProps">
            <NativeDateInput
              :modelValue="slotProps.data.event_date"
              :disabled="submitting || slotProps.data.event_type === 'ROSTER'"
              @date-select="date => handleDateSelection(date, slotProps.data.event_type)"
              aria-label="Attendance date"
            />
          </template>
        </Column>

        <Column field="event_start" header="Start" class="text-600 w-2">
          <template #body="slotProps">
            <div class="flex justify-content-start">
              <div
                v-if="slotProps.data.event_type !== 'ROSTER'"
                class="flex align-items-center justify-content-center mr-2 font-bold surface-100 w-5rem"
              >
                {{ slotProps.data.event_type }}
              </div>
              <NativeTimeInput
                :modelValue="slotProps.data.event_start"
                :showSeconds="slotProps.data.event_type !== 'ROSTER'"
                :disabled="submitting || slotProps.data.event_type === 'ROSTER'"
                @update:model-value="time => handleTimeSelection(time, slotProps.data.event_type)"
                aria-label="Attendance start time"
              />
            </div>
          </template>
        </Column>

        <Column field="event_end" header="End" class="text-600 w-3 pr-2">
          <template #body="slotProps">
            <NativeTimeInput
              v-if="slotProps.data.event_type === 'ROSTER'"
              :modelValue="slotProps.data.event_end"
              disabled
              aria-label="Roster end time"
            />

            <div v-else class="flex align-items-center">
              <Tag
                severity="info"
                class="block p-2 mr-1 w-full"
                :class="statusClass(slotProps.data.attendance_status, slotProps.data.event_type)"
              >
                {{ slotProps.data.attendance_status }}{{ statusDifferenceForType(slotProps.data.event_type) }}
              </Tag>
              <i
                v-if="statusWarning(slotProps.data.event_type)"
                class="pi pi-exclamation-triangle text-yellow-500 ml-1"
                v-tooltip.bottom="`Recheck the ${slotProps.data.event_type === 'IN' ? 'start' : 'end'} time`"
              />
            </div>
          </template>
        </Column>
      </DataTable>

      <Message v-if="formError" severity="error" :closable="false" class="mt-3">{{ formError }}</Message>

      <footer class="flex overflow-hidden mt-3">
        <div class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-start mr-2">
          <Button
            v-if="!includeCheckout"
            label="Add Checkout"
            icon="pi pi-plus"
            class="bg-cyan-700 hover:bg-cyan-500"
            :disabled="submitting"
            @click="addCheckout"
          />
          <Button
            v-else
            label="Remove Checkout"
            icon="pi pi-times"
            class="surface-300 hover:surface-500"
            :disabled="submitting"
            @click="removeCheckout"
          />
        </div>

        <div v-if="approvalRequired" class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-start mr-2">
          <Button
            v-if="!overtimeApproved"
            label="Approve Overtime"
            icon="pi pi-check"
            class="bg-green-700 hover:bg-green-400"
            :disabled="submitting"
            @click="overtimeApproved = true"
          />
          <Button
            v-else
            label="Reject Overtime"
            icon="pi pi-times"
            class="surface-300 hover:surface-500"
            :disabled="submitting"
            @click="overtimeApproved = false"
          />
        </div>

        <div v-if="includeCheckout" class="flex-shrink-1 flex-grow-1 flex align-items-center gap-2 justify-content-start mr-2">
          <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
            <span class="text-xs font-light">Roster: <span class="font-bold">{{ rosterDuration }}</span></span>
          </div>
          <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
            <span class="text-xs font-light">Worked: <span class="font-bold">{{ workedDuration }}</span></span>
          </div>
          <div class="flex surface-100 align-items-center justify-content-center border-round p-2">
            <span class="text-xs font-light">Net: <span class="font-bold">{{ netDuration }}</span></span>
          </div>
        </div>

        <div class="flex-shrink-0 flex align-items-center justify-content-end mr-2">
          <Button label="Cancel" class="surface-200 hover:surface-400" :disabled="submitting" @click="handleBack" />
        </div>
        <div class="flex-shrink-0 flex align-items-center justify-content-end">
          <Button
            label="Mark Attendance"
            icon="pi pi-check"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="submitAttendance"
          />
        </div>
      </footer>
    </div>

    <Dialog v-model:visible="companyDialogVisible" :style="{ width: '20vw' }" :modal="true">
      <template #header>
        <h2 class="w-full m-0 p-0">Select Company</h2>
      </template>
      <Dropdown
        v-model="selectedCompany"
        :options="companiesList"
        filter
        optionLabel="company_name"
        placeholder="Select Company"
        class="w-full"
      />
      <footer class="flex overflow-hidden mt-3">
        <div class="flex-shrink-0 flex align-items-center justify-content-start mr-2">
          <Button label="Cancel" class="surface-200 hover:surface-400" @click="companyDialogVisible = false" />
        </div>
        <div class="flex-grow-1 flex-shrink-1 flex align-items-center justify-content-start">
          <Button label="Apply Company" :disabled="!selectedCompany" @click="applyCompany" />
        </div>
      </footer>
    </Dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import Tooltip from 'primevue/tooltip';
import { useToast } from 'primevue/usetoast';
import NativeDateInput from '@/components/NativeDateInput.vue';
import NativeTimeInput from '@/components/NativeTimeInput.vue';
import { useRosterBulk } from '@/composables/api/useRosterBulk';
import { useAttendanceMark } from '@/composables/api/useAttendanceMark';
import { useUtils } from '@/composables/useUtils';
import { useUserSelections } from '@/composables/useUserSelections';
import {
  parseRosterRouteFilters,
  parseRosterSortQuery
} from '@/composables/useRosterRouteFilters';
import { parseDashboardRouteDate } from '@/composables/useDashboardRoute';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { bulkRosters, loading, error: rosterError, fetchBulkRosters } = useRosterBulk();
const { submitting, markRosterAttendance } = useAttendanceMark();
const { companiesList } = useUserSelections();
const {
  compareTimestamps,
  calculateDuration,
  calculateNetWorkedHours,
  getExactTimestamp,
  parseUTCDate
} = useUtils();

const initialized = ref(false);
const loadError = ref(null);
const checkinTime = ref(null);
const checkoutTime = ref(null);
const includeCheckout = ref(false);
const overtimeApproved = ref(false);
const validationError = ref(null);
const companyDialogVisible = ref(false);
const selectedCompany = ref(null);
const appliedCompany = ref(null);

const rosterId = computed(() => {
  const rawValue = route.params.rosterId;
  if (typeof rawValue !== 'string' || !/^\d+$/.test(rawValue)) return null;
  const value = Number(rawValue);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
});

const roster = computed(() => bulkRosters.value[0] || null);
const isLoading = computed(() => !initialized.value || loading.value);
const rosterStart = computed(() => roster.value ? parseTimestamp(roster.value.roster_start_unix) : null);
const rosterEnd = computed(() => roster.value ? parseTimestamp(roster.value.roster_end_unix) : null);
const checkinStatus = computed(() => compareTimestamps(checkinTime.value, rosterStart.value, 'IN'));
const checkoutStatus = computed(() => compareTimestamps(checkoutTime.value, rosterEnd.value, 'OUT'));
const approvalRequired = computed(() => (
  includeCheckout.value
  && Boolean(checkoutTime.value)
  && (checkinStatus.value === 'EARLY' || checkoutStatus.value === 'OVERTIME')
));
const rosterDuration = computed(() => rosterStart.value && rosterEnd.value
  ? calculateDuration(rosterStart.value, rosterEnd.value)
  : '—');
const workedDuration = computed(() => checkinTime.value && checkoutTime.value
  ? calculateDuration(checkinTime.value, checkoutTime.value)
  : '—');
const netDuration = computed(() => {
  if (!checkinTime.value || !checkoutTime.value || !rosterStart.value || !rosterEnd.value) return '—';
  return calculateNetWorkedHours(
    rosterStart.value,
    rosterEnd.value,
    checkinTime.value,
    checkoutTime.value,
    checkinStatus.value,
    checkoutStatus.value,
    overtimeApproved.value
  );
});

const attendanceRows = computed(() => {
  if (!roster.value || !rosterStart.value || !rosterEnd.value || !checkinTime.value) return [];

  const sharedFields = {
    employee_id: roster.value.employee_id,
    employee_name: roster.value.employee_name,
    employee_company_name: appliedCompany.value?.company_name || roster.value.employee_company_name,
    employee_status: roster.value.employee_status,
    employee_department: roster.value.employee_department
  };
  const rows = [
    createRow(sharedFields, 'ROSTER', 'ROSTER', roster.value.roster_id, rosterStart.value, rosterEnd.value, null),
    createRow(sharedFields, 'IN', roster.value.shift_type, null, checkinTime.value, null, checkinStatus.value)
  ];
  if (includeCheckout.value && checkoutTime.value) {
    rows.push(createRow(sharedFields, 'OUT', roster.value.shift_type, null, checkoutTime.value, null, checkoutStatus.value));
  }
  return rows;
});

const timeSequenceError = computed(() => (
  includeCheckout.value
  && checkinTime.value
  && checkoutTime.value
  && getExactTimestamp(checkoutTime.value) < getExactTimestamp(checkinTime.value)
    ? 'Checkout cannot be earlier than check-in.'
    : null
));
const formError = computed(() => validationError.value || timeSequenceError.value);

const routeError = computed(() => {
  if (!rosterId.value) return 'The roster ID must be a positive integer.';
  if (loadError.value) return loadError.value;
  if (!roster.value) return 'The roster could not be found.';
  if (roster.value.roster_marked) return 'Attendance has already been marked for this roster.';
  if (!['SHIFT', 'COVER'].includes(roster.value.shift_type)) return 'Attendance can only be marked against a shift or cover roster.';
  return null;
});

const canSubmit = computed(() => (
  Boolean(checkinTime.value)
  && (!includeCheckout.value || Boolean(checkoutTime.value))
  && !formError.value
  && !submitting.value
));

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) return null;

  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name === 'DashboardDay') {
    const parsedDate = parseDashboardRouteDate(resolvedRoute.params);
    return parsedDate.valid ? resolvedRoute.fullPath : null;
  }

  if (resolvedRoute.name !== 'ViewRosterFiltered') return null;
  const filters = parseRosterRouteFilters(resolvedRoute.params, 'roster');
  const sort = parseRosterSortQuery(resolvedRoute.query);
  return filters.valid && sort.valid ? resolvedRoute.fullPath : null;
});

function parseTimestamp(timestamp) {
  return parseUTCDate(new Date(Number(timestamp)).toISOString());
}

function createRow(sharedFields, eventType, shiftType, eventId, eventStart, eventEnd, attendanceStatus) {
  return {
    ...sharedFields,
    event_type: eventType,
    shift_type: shiftType,
    event_id: eventId,
    event_start: eventStart,
    event_end: eventEnd,
    event_date: eventStart,
    event_day: eventStart.toLocaleString('default', { weekday: 'long' }),
    event_month: eventStart.toLocaleString('default', { month: 'long' }),
    attendance_status: attendanceStatus
  };
}

function statusClass(status, eventType) {
  return {
    ONTIME: 'surface-300',
    LATE: 'bg-yellow-800',
    EARLY: eventType === 'IN' ? 'bg-cyan-800' : 'bg-red-800',
    OVERTIME: 'bg-green-800'
  }[status] || 'surface-100';
}

function statusDifference(actual, expected, status) {
  if (!actual || !expected || status === 'ONTIME') return '';
  const minutes = Math.round(Math.abs(actual - expected) / 60000);
  return ` (${minutes < 60 ? `${minutes}m` : `${(minutes / 60).toFixed(1)}h`})`;
}

function statusDifferenceForType(eventType) {
  return eventType === 'IN'
    ? statusDifference(checkinTime.value, rosterStart.value, checkinStatus.value)
    : statusDifference(checkoutTime.value, rosterEnd.value, checkoutStatus.value);
}

function statusWarning(eventType) {
  const actual = eventType === 'IN' ? checkinTime.value : checkoutTime.value;
  const expected = eventType === 'IN' ? rosterStart.value : rosterEnd.value;
  return actual && expected ? Math.abs(actual - expected) / 60000 >= 120 : false;
}

function replaceDate(target, selectedDate) {
  const updated = new Date(target);
  updated.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
  return updated;
}

function replaceTime(target, selectedTime) {
  const updated = new Date(target);
  updated.setHours(selectedTime.getHours(), selectedTime.getMinutes(), selectedTime.getSeconds(), selectedTime.getMilliseconds());
  return updated;
}

function handleDateSelection(selectedDate, eventType) {
  if (eventType === 'IN') checkinTime.value = replaceDate(checkinTime.value, selectedDate);
  if (eventType === 'OUT') checkoutTime.value = replaceDate(checkoutTime.value, selectedDate);
}

function handleTimeSelection(selectedTime, eventType) {
  if (eventType === 'IN') checkinTime.value = replaceTime(checkinTime.value, selectedTime);
  if (eventType === 'OUT') checkoutTime.value = replaceTime(checkoutTime.value, selectedTime);
}

function addCheckout() {
  checkoutTime.value = rosterEnd.value ? new Date(rosterEnd.value) : null;
  overtimeApproved.value = false;
  includeCheckout.value = true;
}

function removeCheckout() {
  includeCheckout.value = false;
  checkoutTime.value = null;
  overtimeApproved.value = false;
  validationError.value = null;
}

function openCompanyDialog() {
  selectedCompany.value = appliedCompany.value
    || companiesList.value.find(company => Number(company.company_id) === Number(roster.value.employee_company_id))
    || null;
  companyDialogVisible.value = true;
}

function applyCompany() {
  if (!selectedCompany.value) return;
  appliedCompany.value = selectedCompany.value;
  companyDialogVisible.value = false;
}

function navigateBackOrFallback(replaceFallback = false) {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
  } else if (window.history.state?.back) {
    router.back();
  } else if (replaceFallback) {
    router.replace({ name: 'ViewRoster' });
  } else {
    router.push({ name: 'ViewRoster' });
  }
}

const handleBack = () => navigateBackOrFallback(false);

async function submitAttendance() {
  validationError.value = null;
  const checkinTimestamp = getExactTimestamp(checkinTime.value);
  const checkoutTimestamp = includeCheckout.value ? getExactTimestamp(checkoutTime.value) : null;
  if (checkoutTimestamp !== null && checkoutTimestamp < checkinTimestamp) {
    validationError.value = 'Checkout cannot be earlier than check-in.';
    return;
  }

  const result = await markRosterAttendance({
    roster_id: rosterId.value,
    checkin_timestamp: checkinTimestamp,
    checkout_timestamp: checkoutTimestamp,
    over_below_status: approvalRequired.value && overtimeApproved.value,
    employee_company_id: appliedCompany.value?.company_id || roster.value.employee_company_id
  });
  if (!result.success) {
    validationError.value = result.error;
    return;
  }

  toast.add({ severity: 'success', summary: 'Attendance marked', detail: result.data.message, life: 4000, group: 'br' });
  navigateBackOrFallback(true);
}

onMounted(async () => {
  if (!rosterId.value) {
    initialized.value = true;
    return;
  }

  const loaded = await fetchBulkRosters([rosterId.value]);
  if (!loaded) {
    loadError.value = rosterError.value || 'Failed to load the roster.';
  } else if (roster.value) {
    checkinTime.value = parseTimestamp(roster.value.roster_start_unix);
    checkoutTime.value = null;
    appliedCompany.value = companiesList.value.find(
      company => Number(company.company_id) === Number(roster.value.employee_company_id)
    ) || {
      company_id: roster.value.employee_company_id,
      company_name: roster.value.employee_company_name
    };
  }
  initialized.value = true;
});

watch([checkinTime, checkoutTime, includeCheckout], () => {
  validationError.value = null;
  if (!approvalRequired.value) overtimeApproved.value = false;
});

const vTooltip = Tooltip;
</script>
